import { ReactNode } from "react";
import { Box, Select, MenuItem, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AuthCard } from "./authCard";
import { useLanguage, languages } from "../../contexts/languageContext";
import { AuthFooter } from "./authFooter";

const StyledSelect = styled(Select)(({ theme }) => ({
  color: theme.palette.text.secondary,
  "&.MuiInput-underline:before": {
    borderBottom: "none",
  },
  "& .MuiSelect-select": {
    paddingRight: "24px !important",
  },
  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
  },
}));

const FooterButton = styled(Button)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  textTransform: "none",
  minWidth: "auto",
  padding: theme.spacing(0.5, 1.5),
  "&:hover": {
    backgroundColor: "rgba(232, 234, 237, 0.08)",
  },
}));

interface AuthLayoutProps {
  children: ReactNode;
}

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  const { currentLanguage, setLanguage } = useLanguage();

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        backgroundColor: "#192231",
        overflow: "hidden",
      }}
    >
      {/* Background Grid Pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: "4rem 4rem",
          zIndex: 0,
        }}
      />

      {/* Glowing Lines */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          zIndex: 0,
          "& > div": {
            position: "absolute",
            background: "rgba(255, 255, 255, 0.15)",
            borderRadius: "50%",
            filter: "blur(80px)",
          },
          "& > div:nth-of-type(1)": {
            top: "10%",
            left: "20%",
            width: "30rem",
            height: "30rem",
            opacity: 0.4,
          },
          "& > div:nth-of-type(2)": {
            bottom: "30%",
            right: "10%",
            width: "40rem",
            height: "40rem",
            opacity: 0.3,
          },
        }}
      >
        <div />
        <div />
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          zIndex: 1,
          px: { xs: 1, sm: 2, md: 4, lg: 6 },
          py: { xs: 2, sm: 4, md: 6 },
          minHeight: "calc(100vh - 120px)",
        }}
      >
        <AuthCard>{children}</AuthCard>
      </Box>

      {/* Footer */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <AuthFooter />
      </Box>
    </Box>
  );
};
